import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** The interval ID for automatic playback. @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.refs.pauseButton?.addEventListener('click', this.#handlePauseClick);

    this.current = this.#current;
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.pauseButton?.removeEventListener('click', this.#handlePauseClick);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.#updateButtonLabel();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /** Pauses automatic slide playback. */
  pause() {
    this.paused = true;
    this.suspend();
    this.#updateButtonLabel();
  }

  /** Toggles between play and pause states. */
  toggle() {
    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /** Updates the pause button's aria-label based on current state. */
  #updateButtonLabel() {
    const { pauseButton } = this.refs;
    if (!pauseButton) return;

    const label = this.paused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel;
    if (label) {
      pauseButton.setAttribute('aria-label', label);
    }
  }

  /** Handles pause button click. */
  #handlePauseClick = () => {
    this.toggle();
  };

  /** Suspends automatic slide playback. */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /** Resumes automatic slide playback if autoplay is enabled. */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    const focusableSelector =
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    this.refs.slides?.forEach((slide, index) => {
      const isHidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${isHidden}`);

      if (isHidden) {
        slide.querySelectorAll(focusableSelector).forEach((el) => {
          const current = el.getAttribute('tabindex');
          el.setAttribute('data-announcement-bar-tabindex', current ?? '');
          el.setAttribute('tabindex', '-1');
        });
      } else {
        slide.querySelectorAll('[data-announcement-bar-tabindex]').forEach((el) => {
          const saved = el.getAttribute('data-announcement-bar-tabindex');
          if (saved === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', saved);
          }
          el.removeAttribute('data-announcement-bar-tabindex');
        });
      }
    });
  }

  /** Pause the slideshow when the page is hidden. */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
